import type { AudioFontSchemaType, VideoFontSchemaType } from '@vedalib/editor/lib/brand'
import cn from 'classnames'
import React, { useCallback, useMemo, useRef } from 'react'
import { FileTypeEnum } from 'utils'

import { TooltipCourse } from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import Loader from 'components/uiKit/Loader'
import type { ElementFontCss } from 'services/Branding/types'
import { videoFormat } from 'utils/editorUtils'
import { testProps } from 'utils/test/qaData'

import {
  setVolume,
  setPlayed,
  startSeeking,
  seekTo,
  toggleFullScreen,
  setPlaybackRate,
  showCc,
} from '../store/actions'
import type { IState, Actions } from '../store/types'
import { CaptionsButton } from '../styled/CaptionsButton'
import { MediaPlayerButton } from '../styled/MediaPlayerButton'
import type { IMediaPlayerBaseProps, MediaStyles } from '../types'
import * as s from './Controls.module.scss'
import PlaybackRateSelect from './PlaybackRateSelect'
import SeekSlider from './SeekSlider'
import TimePoints from './TimePoints'
import VolumeSlider from './VolumeSlider'
import { MediaControlType } from './types'
import { useMediaT } from './useMediaT'

interface IControlsProps extends IMediaPlayerBaseProps {
  state: IState
  dispatch: React.Dispatch<Actions>
  styles?: Partial<Record<MediaStyles, React.CSSProperties>>
  fonts?: ElementFontCss<AudioFontSchemaType | VideoFontSchemaType>
  type: 'audio' | 'video'
  isEditor?: boolean
  handleFullScreenIos?: () => void
  full?: boolean
  hasCC: boolean
  duration: number
  urlLoading?: boolean
  handleTogglePlay: () => void
  disableSeeking?: boolean
  maxPlayedSeconds?: number
  displayPlayback?: boolean
  isCourse?: boolean
}

const Controls: React.FC<IControlsProps> = ({
  state,
  dispatch,
  styles,
  type,
  timePoints,
  isEditor,
  handleFullScreenIos,
  fonts,
  full,
  hasCC,
  urlLoading,
  onVolume,
  onPlaybackRate,
  handleTogglePlay,
  disableSeeking,
  maxPlayedSeconds,
  displayPlayback,
  isCourse = false,
}) => {
  const toolTipRef = useRef<HTMLDivElement>(null)
  const tt = useMediaT(isCourse)
  const { playing, played, playedSeconds, playbackRate, volume, loaded, fullscreen, duration } =
    state

  const points = useMemo(
    () => timePoints?.filter((item) => item.time <= duration).sort((a, b) => a.time - b.time) || [],
    [duration, timePoints],
  )

  const currentPoint = useMemo(
    () =>
      points
        .slice()
        .reverse()
        .find(({ time }) => time <= (playedSeconds || 0)),
    [points, playedSeconds],
  )

  const container = fullscreen ? () => toolTipRef.current || document.body : () => document.body

  const clampSeek = useCallback(
    (seconds: number) =>
      disableSeeking ? Math.min(seconds, maxPlayedSeconds ?? Infinity) : seconds,
    [disableSeeking, maxPlayedSeconds],
  )

  const handleStartSeeking = () => dispatch(startSeeking())
  const handleSetPlayed = (seconds: number) =>
    dispatch(setPlayed({ playedSeconds: clampSeek(seconds), duration }))
  const handleSeekTo = useCallback(
    (seconds: number) => dispatch(seekTo(clampSeek(seconds))),
    [dispatch, clampSeek],
  )
  const handleClickCC = () => dispatch(showCc())

  const onVolumeChange = (volume: number) => {
    dispatch(setVolume(volume))
    onVolume?.(volume, duration)
  }
  const onPlaybackRateChange = (playbackRate: number) => {
    onPlaybackRate?.(playbackRate, duration)
    dispatch(setPlaybackRate(playbackRate))
  }
  const handleClickFullscreen = () => {
    handleFullScreenIos?.()
    dispatch(toggleFullScreen())
  }

  const testAttributes = isEditor
    ? {
        play: testProps({ el: 'play', playing }),
        pointsControl: testProps({ el: 'pointsControl', value: timePoints }),
      }
    : undefined

  const finishTime = (
    <div aria-label='Finish time' className={s.finish}>
      {urlLoading ? <Loader name='finishTimeLoader' styleType='svg' /> : videoFormat(duration)}
    </div>
  )
  const audioPreview = !full && type === FileTypeEnum.AUDIO

  if (audioPreview) {
    return (
      <div className={cn(s.controls, { [s.full]: full })}>
        <div className={s.audioPreview}>
          <div className={cn(s.play, { [s.disabled]: duration === 0 })}>
            <TooltipCourse
              container={container()}
              overlay={playing ? tt('elements.media.pause') : tt('elements.media.play')}
              placement='top'
            >
              <MediaPlayerButton
                $styles={styles}
                data-player-control={MediaControlType.Play}
                onClick={() => duration !== 0 && handleTogglePlay()}
                {...testAttributes?.play}
              >
                <Icon name={playing ? 'stopPlay' : 'play2'} size={KitSize.S} />
              </MediaPlayerButton>
            </TooltipCourse>
          </div>
          <div className={s.seek}>
            <SeekSlider
              disableSeeking={disableSeeking}
              duration={duration}
              isCourse={isCourse}
              loaded={loaded}
              maxPlayed={duration > 0 ? (maxPlayedSeconds ?? 0) / duration : 0}
              onSeekTo={handleSeekTo}
              onSetPlayed={handleSetPlayed}
              onStartSeeking={handleStartSeeking}
              played={played}
              points={points}
              styles={styles}
              preview
            />
          </div>
          <div className={s.start}>{videoFormat(playedSeconds)}</div>
          {' / '}
          {finishTime}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(s.controls, {
        [s.inline]: !full && type === FileTypeEnum.VIDEO,
        [s.full]: full,
      })}
    >
      {full ? (
        <div className={s.root}>
          <div className={s.play}>
            <TooltipCourse
              container={container()}
              overlay={playing ? tt('elements.media.pause') : tt('elements.media.play')}
              placement='top'
            >
              <MediaPlayerButton
                $styles={styles}
                aria-keyshortcuts='space'
                aria-label='Pause'
                aria-pressed={!playing}
                data-player-control={MediaControlType.Play}
                onClick={handleTogglePlay}
                {...testAttributes?.play}
              >
                <Icon name={playing ? 'otherPause' : 'otherPlay'} size={KitSize.S} aria-hidden />
              </MediaPlayerButton>
            </TooltipCourse>
          </div>
          {displayPlayback && (
            <div className={s.time}>
              <div aria-label='Current time' className={s.start}>
                {videoFormat(playedSeconds)}
              </div>
              {' / '}
              {finishTime}
            </div>
          )}
          <div className={s.seek}>
            {currentPoint && (
              <div
                className={s.tooltip}
                style={{
                  ...fonts?.tooltip,
                  left: `${(currentPoint.time / duration) * 100}%`,
                }}
                {...testProps({ el: 'tooltipControl', value: currentPoint.title })}
              >
                {currentPoint.title}
              </div>
            )}
            <SeekSlider
              currentPoint={currentPoint}
              disableSeeking={disableSeeking}
              duration={duration}
              isCourse={isCourse}
              loaded={loaded}
              maxPlayed={duration > 0 ? (maxPlayedSeconds ?? 0) / duration : 0}
              onSeekTo={handleSeekTo}
              onSetPlayed={handleSetPlayed}
              onStartSeeking={handleStartSeeking}
              played={played}
              points={points}
              styles={styles}
            />
          </div>
          {Boolean(points?.length) && (
            <div
              className={s.points}
              key={`timePoints-${fullscreen?.toString()}`}
              style={styles?.icons}
              {...testAttributes?.pointsControl}
            >
              <TimePoints
                container={container}
                disableSeeking={disableSeeking}
                dispatch={dispatch}
                isCourse={isCourse}
                maxPlayedSeconds={maxPlayedSeconds}
                timePoints={points}
              />
            </div>
          )}
          <div className={s.volume}>
            <VolumeSlider
              container={container}
              dispatch={dispatch}
              isCourse={isCourse}
              muted={state.muted}
              onChange={onVolumeChange}
              styles={styles}
              value={volume}
            />
          </div>
          {hasCC && (
            <div className={cn(s.buttons, s.cc)}>
              <TooltipCourse overlay={tt('uiKit.tooltip.captions')} placement='top'>
                <CaptionsButton
                  $active={state.showCc}
                  $styles={styles}
                  aria-keyshortcuts='c'
                  aria-label='Show captions'
                  aria-pressed={state.showCc}
                  data-player-control={MediaControlType.Captions}
                  onClick={handleClickCC}
                >
                  <Icon name='cc' size={KitSize.S} aria-hidden />
                </CaptionsButton>
              </TooltipCourse>
            </div>
          )}
          <div
            className={cn(s.buttons, s.rate)}
            key={`playbackRate-${fullscreen?.toString()}`}
            style={styles?.icons}
          >
            <PlaybackRateSelect
              container={container}
              isCourse={isCourse}
              onChange={onPlaybackRateChange}
              value={playbackRate}
            />
          </div>
          {type === 'video' && (
            <div className={cn(s.buttons, s.fullscreen)}>
              <TooltipCourse
                container={container()}
                overlay={
                  fullscreen ? tt('elements.media.exitFullscreen') : tt('elements.media.fullscreen')
                }
                placement='top-end'
              >
                <MediaPlayerButton
                  $styles={styles}
                  aria-keyshortcuts='f'
                  aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                  data-player-control={MediaControlType.Fullscreen}
                  onClick={handleClickFullscreen}
                >
                  <Icon
                    name={fullscreen ? 'otherSmallScreen' : 'otherFullScreen'}
                    size={KitSize.S}
                    aria-hidden
                  />
                </MediaPlayerButton>
              </TooltipCourse>
            </div>
          )}
          <div ref={toolTipRef} />
        </div>
      ) : (
        <SeekSlider
          disableSeeking={disableSeeking}
          duration={duration}
          isCourse={isCourse}
          loaded={loaded}
          maxPlayed={duration > 0 ? (maxPlayedSeconds ?? 0) / duration : 0}
          onSeekTo={handleSeekTo}
          onSetPlayed={handleSetPlayed}
          onStartSeeking={handleStartSeeking}
          played={played}
          points={points}
          styles={styles}
        />
      )}
    </div>
  )
}

export default Controls
